import { Injectable } from '@angular/core';
import { BehaviorSubject, fromEvent, Observable, Subject } from 'rxjs';
import { Order } from 'src/app/shared/models/orders';
import { RejectReason } from 'src/app/shared/models/RejectReason';
import { ReturnReason } from 'src/app/shared/models/returnReason';
import {
  OFFLINE_OP_LABELS,
  PendingSubmissionRow,
  TERMINAL_OFFLINE_OP_TYPES,
} from './pending-submission.model';
import { CorrelationContextService } from 'src/core/logging/correlation-context.service';
import { randomUuid } from 'src/core/logging/uuid.util';

// ─── Queue types ─────────────────────────────────────────────────────────────

export interface StatusUpdatePayload {
  tkt: string;
  orderNumber: string;
  status: number;
  capturedHour: string; // HH:mm:ss captured at click time, not sync time
}

export interface ExtraValuesPayload {
  tkt: string;
  orderNumber: string;
  waterAddition: boolean;
  anotherAddition: boolean;
  guidingRemarks: string;
  m3Returned: number;
  reasonToReturn: string;
  /** Campo legado que la UI presenta como "Sector (opcional)". */
  m3ReturnedPallet: number | string;
  files: { name: string; base64: string }[];
}

export type ExtraField = 'waterAddition' | 'anotherAddition' | 'guidingRemarks' | 'm3Returned';

export interface ExtraFieldPayload {
  tkt: string;
  orderNumber: string;
  field: ExtraField;
  value: string;
}

export interface UnsignedPayload {
  tkt: string;
  orderNumber: string;
}

export interface ClearPendingPayload {
  tkt: string;
  orderNumber: string;
  commentsFinal?: string;
  finFlujo?: boolean;
}

export interface SignedPayload {
  tkt: string;
  orderNumber: string;
  rut: string;
  name: string;
  signature: string;
  email: string;
  emailDos?: string;
  tipoFirma?: 'RECEPCION' | 'ADICION_AGUA' | 'DEVOLUCION_HORMIGON';
  comentario?: string;
}

export interface RejectedPayload {
  tkt: string;
  orderNumber: string;
  rejectReason: string;
  rejectDescription: string;
  rutClient: string;
  isMixer: boolean;
  files: { name: string; base64: string }[];
}

export type OfflineOpStatus = 'pending' | 'syncing' | 'failed' | 'blocked' | 'paused';

export interface OfflineOperationMeta {
  userRut?: string;
  queuedAt?: number;
  failingSince?: number;
  backedUp?: boolean;
  retryCount?: number;
  lastAttemptAt?: number;
  lastErrorMessage?: string;
  status?: OfflineOpStatus;
  /** UUID v4 — generado una vez al encolar. */
  operationId?: string;
  /** ID de flujo funcional compartido con logs. */
  correlationId?: string;
  lastAttemptId?: string;
  monitoringRegistered?: boolean;
}

export type OfflineOperation =
  | ({ type: 'statusUpdate'; payload: StatusUpdatePayload } & OfflineOperationMeta)
  | ({ type: 'extraValues';  payload: ExtraValuesPayload } & OfflineOperationMeta)
  | ({ type: 'extraField';   payload: ExtraFieldPayload } & OfflineOperationMeta)
  | ({ type: 'unsigned';     payload: UnsignedPayload } & OfflineOperationMeta)
  | ({ type: 'clearPending'; payload: ClearPendingPayload } & OfflineOperationMeta)
  | ({ type: 'signed';       payload: SignedPayload } & OfflineOperationMeta)
  | ({ type: 'signedCanvas'; payload: SignedPayload } & OfflineOperationMeta)
  | ({ type: 'rejected';     payload: RejectedPayload } & OfflineOperationMeta);

export interface OfflineQueueEntry {
  tkt: string;
  timestamp: number;
  operations: OfflineOperation[];
}

// ─── Store names ─────────────────────────────────────────────────────────────

const DB_NAME    = 'polpaico-offline';
const DB_VERSION = 1;

const STORE = {
  ORDERS:         'orders',
  ORDER_DETAILS:  'order-details',
  PDFS:           'pdfs',
  REFERENCE_DATA: 'reference-data',
  QUEUE:          'offline-queue',
} as const;

// ─── Service ──────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class OfflineStorageService {

  private dbPromise: Promise<IDBDatabase>;
  private onlineSubject          = new BehaviorSubject<boolean>(navigator.onLine);
  private queueCountSubject      = new BehaviorSubject<number>(0);
  private operationQueuedSubject = new Subject<void>();
  private queueChangedSubject    = new Subject<void>();

  readonly online$:          Observable<boolean> = this.onlineSubject.asObservable();
  readonly queueCount$:      Observable<number>  = this.queueCountSubject.asObservable();
  readonly operationQueued$: Observable<void>    = this.operationQueuedSubject.asObservable();
  readonly queueChanged$:    Observable<void>    = this.queueChangedSubject.asObservable();

  constructor(private readonly correlationContext: CorrelationContextService) {
    this.dbPromise = this.openDB();
    this.dbPromise.then(() => this.refreshQueueCount());

    // Ask the browser to treat our IndexedDB as persistent so the outgoing queue
    // can't be evicted under storage pressure. Harmless if unsupported (older WebView).
    this.requestPersistentStorage();

    fromEvent(window, 'online').subscribe(() => this.onlineSubject.next(true));
    fromEvent(window, 'offline').subscribe(() => this.onlineSubject.next(false));
  }

  private async requestPersistentStorage(): Promise<void> {
    try {
      const storage = (navigator as any).storage;
      if (storage && typeof storage.persist === 'function') {
        const granted: boolean = await storage.persist();
        // console.log(`[OfflineStorage] Persistent storage ${granted ? 'granted' : 'denied'}`);
      }
    } catch (e) {
      console.warn('[OfflineStorage] navigator.storage.persist unavailable:', e);
    }
  }

  /**
   * Reads the currently-logged-in user's RUT so queue ops can be stamped.
   *
   * IMPORTANT: returns a trimmed string. RUT_USUARIO can come back from the backend
   * as either a string or a number (account.service maps RUT_M, which is type-unstable).
   * Stamping and comparing must happen on the same canonical form, or strict !== will
   * always mismatch and silently block the queue.
   */
  private getCurrentUserRut(): string {
    try {
      const user = JSON.parse(localStorage.getItem('user') ?? 'null');
      const raw = user?.RUT_USUARIO;
      if (raw === null || raw === undefined) return '';
      return String(raw).trim();
    } catch {
      return '';
    }
  }

  private async refreshQueueCount(): Promise<void> {
    const queue = await this.getQueue();
    const completeCount = queue.filter(entry =>
      entry.operations.some(op =>
        op.type === 'signed' ||
        op.type === 'signedCanvas' ||
        op.type === 'unsigned' ||
        op.type === 'clearPending' ||
        op.type === 'rejected'
      )
    ).length;
    this.queueCountSubject.next(completeCount);
  }

  isOnline(): boolean {
    return navigator.onLine;
  }

  // ─── DB init ────────────────────────────────────────────────────────────────

  private openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);

      req.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        for (const name of Object.values(STORE)) {
          if (!db.objectStoreNames.contains(name)) {
            // offline-queue uses keyPath so the tkt is embedded in the record
            const opts = name === STORE.QUEUE ? { keyPath: 'tkt' } : undefined;
            db.createObjectStore(name, opts);
          }
        }
      };

      req.onsuccess = (e) => resolve((e.target as IDBOpenDBRequest).result);
      req.onerror   = (e) => reject((e.target as IDBOpenDBRequest).error);
    });
  }

  // ─── Generic helpers ────────────────────────────────────────────────────────

  private async idbGet<T>(store: string, key: string): Promise<T | null> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const req = db.transaction(store, 'readonly').objectStore(store).get(key);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror   = () => reject(req.error);
    });
  }

  private async idbSet<T>(store: string, key: string, value: T): Promise<void> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const req = db.transaction(store, 'readwrite').objectStore(store).put(value, key);
      req.onsuccess = () => resolve();
      req.onerror   = () => reject(req.error);
    });
  }

  private async idbDelete(store: string, key: string): Promise<void> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const req = db.transaction(store, 'readwrite').objectStore(store).delete(key);
      req.onsuccess = () => resolve();
      req.onerror   = () => reject(req.error);
    });
  }

  private async idbGetAll<T>(store: string): Promise<T[]> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const req = db.transaction(store, 'readonly').objectStore(store).getAll();
      req.onsuccess = () => resolve(req.result ?? []);
      req.onerror   = () => reject(req.error);
    });
  }

  private async idbGetAllKeys(store: string): Promise<string[]> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const req = db.transaction(store, 'readonly').objectStore(store).getAllKeys();
      req.onsuccess = () => resolve((req.result ?? []).map(k => String(k)));
      req.onerror   = () => reject(req.error);
    });
  }

  private async idbClear(store: string): Promise<void> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const req = db.transaction(store, 'readwrite').objectStore(store).clear();
      req.onsuccess = () => resolve();
      req.onerror   = () => reject(req.error);
    });
  }

  // ─── Orders list ────────────────────────────────────────────────────────────

  saveOrders(orders: Order[]): Promise<void> {
    return this.idbSet(STORE.ORDERS, 'list', orders);
  }

  getOrders(): Promise<Order[] | null> {
    return this.idbGet<Order[]>(STORE.ORDERS, 'list');
  }

  // ─── Order detail ───────────────────────────────────────────────────────────

  saveOrderDetail(tkt: string, order: Order): Promise<void> {
    return this.idbSet(STORE.ORDER_DETAILS, tkt, order);
  }

  getOrderDetail(tkt: string): Promise<Order | null> {
    return this.idbGet<Order>(STORE.ORDER_DETAILS, tkt);
  }

  // ─── PDFs ───────────────────────────────────────────────────────────────────

  savePdf(tkt: string, base64: string, filename: string = 'guia.pdf'): Promise<void> {
    return this.idbSet(STORE.PDFS, tkt, { base64, filename });
  }

  async getPdf(tkt: string): Promise<{ base64: string; filename: string } | null> {
    const stored = await this.idbGet<{ base64: string; filename: string } | string>(STORE.PDFS, tkt);
    if (!stored) return null;
    // Backward-compat: old entries were stored as a plain base64 string
    if (typeof stored === 'string') return { base64: stored, filename: 'guia.pdf' };
    return stored;
  }

  // ─── Reference data ─────────────────────────────────────────────────────────

  saveReturnReasons(data: ReturnReason[]): Promise<void> {
    return this.idbSet(STORE.REFERENCE_DATA, 'returnReasons', data);
  }

  getReturnReasons(): Promise<ReturnReason[] | null> {
    return this.idbGet<ReturnReason[]>(STORE.REFERENCE_DATA, 'returnReasons');
  }

  saveRejectReasons(data: RejectReason[]): Promise<void> {
    return this.idbSet(STORE.REFERENCE_DATA, 'rejectReasons', data);
  }

  getRejectReasons(): Promise<RejectReason[] | null> {
    return this.idbGet<RejectReason[]>(STORE.REFERENCE_DATA, 'rejectReasons');
  }

  // ─── Offline queue ──────────────────────────────────────────────────────────

  async getQueue(): Promise<OfflineQueueEntry[]> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE.QUEUE, 'readwrite');
      const store = tx.objectStore(STORE.QUEUE);
      const req = store.getAll();
      let queue: OfflineQueueEntry[] = [];

      req.onsuccess = () => {
        queue = req.result ?? [];
        for (const entry of queue) {
          let changed = false;
          for (const op of entry.operations ?? []) {
            if (!op.operationId) {
              op.operationId = randomUuid();
              changed = true;
            }
          }
          if (changed) {
            store.put(entry);
          }
        }
      };
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => resolve(queue);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  }

  /** True si el tkt tiene alguna operacion pendiente de los tipos indicados. */
  async hasPendingOp(
    tkt: string,
    types: OfflineOperation['type'][]
  ): Promise<boolean> {
    const queue = await this.getQueue();
    const entry = queue.find((e) => e.tkt === tkt);
    if (!entry) {
      return false;
    }
    return entry.operations.some((op) => types.includes(op.type));
  }

  /**
   * Appends a single operation to the queue entry for a given tkt, atomically.
   *
   * Runs read-modify-write inside a single IndexedDB transaction so it cannot race
   * with SyncService's removeFirstOperation. Each op is stamped with the RUT of the
   * user currently logged in — the SyncService refuses to send an op whose userRut
   * doesn't match the active session (prevents user A's queued signature from being
   * transmitted under user B's auth key if the tablet changes hands).
   *
   * Operations are kept in insertion order — the SyncService sends them sequentially.
   */
  async addOperationToQueue(tkt: string, operation: OfflineOperation): Promise<void> {
    const correlationId =
      operation.correlationId ??
      this.correlationContext.getCorrelationId() ??
      randomUuid();
    const operationId = operation.operationId ?? randomUuid();
    const stamped: OfflineOperation = {
      ...operation,
      operationId,
      correlationId,
      userRut: operation.userRut !== undefined && operation.userRut !== null
        ? String(operation.userRut).trim()
        : this.getCurrentUserRut(),
      queuedAt: operation.queuedAt ?? Date.now(),
      retryCount: operation.retryCount ?? 0,
      status: operation.status ?? 'pending',
      monitoringRegistered: operation.monitoringRegistered ?? false,
    };

    const db = await this.dbPromise;
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE.QUEUE, 'readwrite');
      const store = tx.objectStore(STORE.QUEUE);
      const getReq = store.get(tkt);
      getReq.onsuccess = () => {
        const existing = getReq.result as OfflineQueueEntry | undefined;
        const entry: OfflineQueueEntry = existing ?? {
          tkt, timestamp: Date.now(), operations: [],
        };

        if (TERMINAL_OFFLINE_OP_TYPES.includes(stamped.type)) {
          const duplicate = entry.operations.some((op) => {
            if (op.type !== stamped.type) return false;
            if (stamped.type !== 'signedCanvas' || op.type !== 'signedCanvas') {
              return true;
            }
            const existingTipo = op.payload.tipoFirma || 'RECEPCION';
            const nextTipo = stamped.payload.tipoFirma || 'RECEPCION';
            return existingTipo === nextTipo;
          });
          if (duplicate) {
            reject(new Error(
              `Ya existe una operación pendiente de tipo "${OFFLINE_OP_LABELS[stamped.type]}" para esta guía.`
            ));
            return;
          }
        }

        // Coalesce extras: keep only the latest snapshot for this ticket.
        if (stamped.type === 'extraValues') {
          entry.operations = entry.operations.filter((op) => op.type !== 'extraValues');
        }
        if (stamped.type === 'extraField') {
          const field = stamped.payload.field;
          entry.operations = entry.operations.filter(
            (op) => !(op.type === 'extraField' && op.payload.field === field)
          );
        }

        entry.operations.push(stamped);
        const putReq = store.put(entry);
        putReq.onsuccess = () => resolve();
        putReq.onerror   = () => reject(putReq.error);
      };
      getReq.onerror = () => reject(getReq.error);
    });

    await this.refreshQueueCount();
    this.operationQueuedSubject.next();
    this.emitQueueChanged();
  }

  /**
   * Atomically removes the first operation from a tkt's queue entry. If the entry
   * is empty after removal, the whole entry is deleted.
   *
   * This is the pair to addOperationToQueue: because both operate inside a single
   * IndexedDB transaction and ops are always appended, the head of the persisted list
   * always matches the op the SyncService just finished processing — even if a new op
   * was queued concurrently during the flush.
   */
  async removeFirstOperation(tkt: string): Promise<void> {
    const db = await this.dbPromise;
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE.QUEUE, 'readwrite');
      const store = tx.objectStore(STORE.QUEUE);
      const getReq = store.get(tkt);
      getReq.onsuccess = () => {
        const entry = getReq.result as OfflineQueueEntry | undefined;
        if (!entry || entry.operations.length === 0) { resolve(); return; }
        entry.operations.shift();
        if (entry.operations.length === 0) {
          const delReq = store.delete(tkt);
          delReq.onsuccess = () => resolve();
          delReq.onerror   = () => reject(delReq.error);
        } else {
          const putReq = store.put(entry);
          putReq.onsuccess = () => resolve();
          putReq.onerror   = () => reject(putReq.error);
        }
      };
      getReq.onerror = () => reject(getReq.error);
    });
    await this.refreshQueueCount();
    this.emitQueueChanged();
  }

  /** Filas aplanadas para el listado de envíos pendientes (5.3). */
  async getPendingSubmissions(): Promise<PendingSubmissionRow[]> {
    const queue = await this.getQueue();
    const rows: PendingSubmissionRow[] = [];

    for (const entry of queue) {
      for (const op of entry.operations) {
        const folio = await this.lookupFolio(entry.tkt);
        const orderNumber = this.extractOrderNumber(op);
        rows.push({
          operationId: op.operationId as string,
          tkt: entry.tkt,
          orderNumber,
          folio,
          operationType: op.type,
          operationLabel: OFFLINE_OP_LABELS[op.type],
          queuedAt: op.queuedAt ?? entry.timestamp,
          retryCount: op.retryCount ?? 0,
          lastAttemptAt: op.lastAttemptAt,
          status: op.status ?? 'pending',
          lastErrorMessage: op.lastErrorMessage,
          userRut: op.userRut,
        });
      }
    }

    return rows.sort((a, b) => b.queuedAt - a.queuedAt);
  }

  /** Registra un intento de envío (incrementa retryCount, marca syncing). */
  async markOperationAttempt(tkt: string, queuedAt: number | undefined): Promise<void> {
    if (queuedAt === undefined) return;
    await this.updateOperationByQueuedAt(tkt, queuedAt, (op) => {
      op.retryCount = (op.retryCount ?? 0) + 1;
      op.lastAttemptAt = Date.now();
      op.status = 'syncing';
    });
    this.emitQueueChanged();
  }

  /** Persiste fallo de envío con mensaje legible. */
  async markOperationFailed(
    tkt: string,
    queuedAt: number | undefined,
    errorMessage: string,
  ): Promise<void> {
    if (queuedAt === undefined) return;
    const now = Date.now();
    await this.updateOperationByQueuedAt(tkt, queuedAt, (op) => {
      op.status = 'failed';
      op.lastErrorMessage = errorMessage;
      if (op.failingSince === undefined) {
        op.failingSince = now;
      }
    });
    this.emitQueueChanged();
  }

  /** Marca op bloqueada (user mismatch, sin token) sin contar como fallo de red. */
  async markOperationBlocked(
    tkt: string,
    queuedAt: number | undefined,
    reason: string,
  ): Promise<void> {
    if (queuedAt === undefined) return;
    await this.updateOperationByQueuedAt(tkt, queuedAt, (op) => {
      op.status = 'blocked';
      op.lastErrorMessage = reason;
    });
    this.emitQueueChanged();
  }

  /** Pausa una operacion que aun no esta siendo enviada. */
  async pauseOperation(tkt: string, operationId: string): Promise<boolean> {
    const changed = await this.updateOperationById(tkt, operationId, (op) => {
      const current = op.status ?? 'pending';
      if (current === 'syncing' || current === 'paused') {
        return false;
      }
      op.status = 'paused';
      return true;
    });
    if (changed) this.emitQueueChanged();
    return changed;
  }

  /** Reanuda una operacion pausada, dejandola disponible para el siguiente flush. */
  async resumeOperation(tkt: string, operationId: string): Promise<boolean> {
    const changed = await this.updateOperationById(tkt, operationId, (op) => {
      if (op.status !== 'paused' && op.status !== 'blocked') {
        return false;
      }
      op.status = 'pending';
      return true;
    });
    if (changed) this.emitQueueChanged();
    return changed;
  }

  async getOperation(tkt: string, operationId: string): Promise<OfflineOperation | null> {
    const entry = await this.idbGet<OfflineQueueEntry>(STORE.QUEUE, tkt);
    return entry?.operations.find((op) => op.operationId === operationId) ?? null;
  }

  /** Actualiza solo el contenido editable; identificadores y metadatos se preservan. */
  async updatePausedOperationPayload(
    tkt: string,
    operationId: string,
    payload: OfflineOperation['payload'],
  ): Promise<boolean> {
    const changed = await this.updateOperationById(tkt, operationId, (op) => {
      if (op.status !== 'paused' && op.status !== 'blocked') {
        return false;
      }
      const currentPayload = op.payload as { tkt: string; orderNumber: string };
      op.payload = {
        ...payload,
        tkt: currentPayload.tkt,
        orderNumber: currentPayload.orderNumber,
      } as OfflineOperation['payload'];
      op.lastErrorMessage = undefined;
      op.failingSince = undefined;
      op.backedUp = false;
      return true;
    });
    if (changed) this.emitQueueChanged();
    return changed;
  }

  /** Reclama atomicamente una operacion para envio, evitando carreras con Pausar. */
  async tryMarkOperationAttempt(tkt: string, operationId: string): Promise<boolean> {
    const changed = await this.updateOperationById(tkt, operationId, (op) => {
      const current = op.status ?? 'pending';
      // `syncing` puede haber quedado persistido tras un cierre abrupto. SyncService
      // deduplica los flush activos, por lo que es seguro reclamarlo nuevamente.
      if (current === 'paused' || current === 'blocked') {
        return false;
      }
      op.retryCount = (op.retryCount ?? 0) + 1;
      op.lastAttemptAt = Date.now();
      op.status = 'syncing';
      return true;
    });
    if (changed) this.emitQueueChanged();
    return changed;
  }

  private emitQueueChanged(): void {
    this.queueChangedSubject.next();
  }

  private async updateOperationById(
    tkt: string,
    operationId: string,
    updater: (op: OfflineOperation) => boolean,
  ): Promise<boolean> {
    const db = await this.dbPromise;
    return new Promise<boolean>((resolve, reject) => {
      const tx = db.transaction(STORE.QUEUE, 'readwrite');
      const store = tx.objectStore(STORE.QUEUE);
      const getReq = store.get(tkt);
      let changed = false;

      getReq.onsuccess = () => {
        const entry = getReq.result as OfflineQueueEntry | undefined;
        const op = entry?.operations.find((item) => item.operationId === operationId);
        if (!entry || !op) {
          return;
        }
        changed = updater(op);
        if (changed) {
          store.put(entry);
        }
      };
      getReq.onerror = () => reject(getReq.error);
      tx.oncomplete = () => resolve(changed);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  }

  private extractOrderNumber(op: OfflineOperation): string {
    const payload = op.payload as { orderNumber?: string };
    return payload?.orderNumber?.toString() ?? '—';
  }

  private async updateOperationByQueuedAt(
    tkt: string,
    queuedAt: number,
    updater: (op: OfflineOperation) => void,
  ): Promise<void> {
    const db = await this.dbPromise;
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE.QUEUE, 'readwrite');
      const store = tx.objectStore(STORE.QUEUE);
      const getReq = store.get(tkt);
      getReq.onsuccess = () => {
        const entry = getReq.result as OfflineQueueEntry | undefined;
        if (!entry) { resolve(); return; }
        const op = entry.operations.find((o) => o.queuedAt === queuedAt);
        if (!op) { resolve(); return; }
        updater(op);
        const putReq = store.put(entry);
        putReq.onsuccess = () => resolve();
        putReq.onerror   = () => reject(putReq.error);
      };
      getReq.onerror = () => reject(getReq.error);
    });
  }
  //
  // Writes a JSON (and PNG for signed ops) of a queued operation into the device's
  // public Downloads folder, so the captured signature/photos survive an app-data
  // wipe or reinstall. The trigger policy (offline-queue / stuck >5 min) lives in
  // SyncService — we only do the write+marking here.
  //
  // Cleanup note: the WebView has no API to delete files it created, so backups stay
  // until the user removes them via the device file manager. The trigger policy keeps
  // accumulation tiny in practice (only failures produce files).

  /**
   * Writes the backup file(s) for a queued op and marks the op as backedUp so we
   * never duplicate. Pulls the folio number from the cached order so the filename
   * is human-meaningful.
   */
  async backupOperation(tkt: string, op: OfflineOperation): Promise<void> {
    if (op.backedUp) return;
    if (!this.shouldBackup(op)) return;

    try {
      const folio = await this.lookupFolio(tkt);
      const baseName = `polpaico-firma-${folio}-${this.fileTimestamp(op.queuedAt)}`;

      // JSON: full op metadata + payload (signature/files inline as base64).
      const jsonContent = JSON.stringify({
        type: op.type,
        queuedAt: op.queuedAt,
        userRut: op.userRut,
        tkt,
        folio,
        payload: op.payload,
      }, null, 2);
      this.triggerDownload(
        new Blob([jsonContent], { type: 'application/json' }),
        `${baseName}.json`,
      );

      // PNG companion for signed ops, so a manager can eyeball the signature
      // without having to parse JSON.
      if (
        (op.type === 'signed' || op.type === 'signedCanvas') &&
        (op.payload as SignedPayload).signature
      ) {
        const pngBlob = this.dataUriToBlob((op.payload as SignedPayload).signature);
        if (pngBlob) {
          this.triggerDownload(pngBlob, `${baseName}.png`);
        }
      }

      await this.markOperationBackedUp(tkt, op.queuedAt);
      // console.log(`[OfflineStorage] backed up op for tkt=${tkt} folio=${folio} type=${op.type}`);
    } catch (e) {
      console.warn(`[OfflineStorage] backupOperation failed for tkt ${tkt}:`, e);
    }
  }

  /** Only signature-bearing ops carry irreplaceable data worth backing up. */
  private shouldBackup(op: OfflineOperation): boolean {
    return op.type === 'signed' || op.type === 'signedCanvas' || op.type === 'rejected' || op.type === 'extraValues' || op.type === 'extraField';
  }

  /**
   * Stamps the op with `failingSince` the first time a flush attempt fails for it
   * while the device is online. Subsequent failures don't update it (the timer
   * tracks "how long has this been failing on a healthy connection", not "when did
   * the most recent failure happen"). The stamp is cleared implicitly: a successful
   * send removes the op from the queue, so by definition the field only exists on
   * ops that have failed and not yet recovered.
   *
   * Atomic single-tx so it can't race with addOperationToQueue or removeFirstOperation.
   */
  async markOperationFailingSince(tkt: string, queuedAt: number | undefined, ts: number): Promise<void> {
    if (queuedAt === undefined) return;
    const db = await this.dbPromise;
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE.QUEUE, 'readwrite');
      const store = tx.objectStore(STORE.QUEUE);
      const getReq = store.get(tkt);
      getReq.onsuccess = () => {
        const entry = getReq.result as OfflineQueueEntry | undefined;
        if (!entry) { resolve(); return; }
        const op = entry.operations.find(o => o.queuedAt === queuedAt);
        if (!op) { resolve(); return; }
        if (op.failingSince !== undefined) { resolve(); return; } // already stamped — preserve original
        op.failingSince = ts;
        const putReq = store.put(entry);
        putReq.onsuccess = () => resolve();
        putReq.onerror   = () => reject(putReq.error);
      };
      getReq.onerror = () => reject(getReq.error);
    });
  }

  /**
   * Sets `backedUp = true` on the matching op (identified by queuedAt) within an
   * entry. Atomic single-tx so it doesn't race with addOperationToQueue or
   * removeFirstOperation. queuedAt is unique within an entry (millisecond stamp +
   * append-only ordering), so no further identifier is needed.
   */
  async markOperationBackedUp(tkt: string, queuedAt: number | undefined): Promise<void> {
    if (queuedAt === undefined) return;
    const db = await this.dbPromise;
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE.QUEUE, 'readwrite');
      const store = tx.objectStore(STORE.QUEUE);
      const getReq = store.get(tkt);
      getReq.onsuccess = () => {
        const entry = getReq.result as OfflineQueueEntry | undefined;
        if (!entry) { resolve(); return; }
        const op = entry.operations.find(o => o.queuedAt === queuedAt);
        if (!op) { resolve(); return; }
        op.backedUp = true;
        const putReq = store.put(entry);
        putReq.onsuccess = () => resolve();
        putReq.onerror   = () => reject(putReq.error);
      };
      getReq.onerror = () => reject(getReq.error);
    });
  }

  /** Número de guía de despacho (folio) asociado al ticket, si está en caché. */
  async lookupGuiaNumber(tkt: string): Promise<string | undefined> {
    try {
      const detail = await this.getOrderDetail(tkt);
      const fromDetail = (detail as Order | null)?.folioNumberGD;
      if (fromDetail != null) {
        const guia = String(fromDetail).trim();
        if (guia) {
          return guia;
        }
      }
      const list = await this.getOrders();
      const match = list?.find((o) => String(o.tkt) === String(tkt));
      const fromList = match?.folioNumberGD;
      if (fromList != null) {
        const guia = String(fromList).trim();
        if (guia) {
          return guia;
        }
      }
    } catch {
      /* ignore */
    }
    return undefined;
  }

  /** Looks up the user-visible guía folio number from the cached order, falling
   *  back to the tkt if no order is cached (still gives a unique-per-guía filename). */
  private async lookupFolio(tkt: string): Promise<string> {
    return (await this.lookupGuiaNumber(tkt)) ?? `tkt${tkt}`;
  }

  /** Filesystem-safe timestamp (no `:` or `.` — Android disallows in MediaStore names). */
  private fileTimestamp(ms: number | undefined): string {
    const d = new Date(ms ?? Date.now());
    return d.toISOString().replace(/[:.]/g, '-');
  }

  /** Converts a data-URI (`data:image/png;base64,...`) into a Blob, or null if malformed. */
  private dataUriToBlob(dataUri: string): Blob | null {
    try {
      const [header, data] = dataUri.split(',');
      const mime = header.match(/:(.*?);/)?.[1] ?? 'image/png';
      const bin = atob(data);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      return new Blob([arr], { type: mime });
    } catch {
      return null;
    }
  }

  /** Same `<a download>` blob pattern the PDF viewer uses; lands in public Downloads. */
  private triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    a.setAttribute('target', '_blank');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Defer revoke so the WebView download manager has time to fetch the blob.
    setTimeout(() => URL.revokeObjectURL(url), 8000);
  }

  async saveQueueEntry(entry: OfflineQueueEntry): Promise<void> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      // keyPath is 'tkt', so no separate key argument needed
      const req = db.transaction(STORE.QUEUE, 'readwrite')
        .objectStore(STORE.QUEUE)
        .put(entry);
      req.onsuccess = () => resolve();
      req.onerror   = () => reject(req.error);
    });
  }

  async removeQueueEntry(tkt: string): Promise<void> {
    await this.idbDelete(STORE.QUEUE, tkt);
    await this.refreshQueueCount();
  }

  async hasQueuedItems(): Promise<boolean> {
    const queue = await this.getQueue();
    return queue.length > 0;
  }

  /**
   * Deletes the order detail and PDF cache for a tkt.
   * Called by SyncService after a signed/unsigned operation is successfully sent,
   * and by CacheWarmupService when a tkt is no longer in "Despachos Actuales".
   */
  async clearTktCache(tkt: string): Promise<void> {
    await Promise.all([
      this.idbDelete(STORE.ORDER_DETAILS, tkt),
      this.idbDelete(STORE.PDFS, tkt),
    ]);
  }

  /**
   * Clears all cached data stores (orders, details, PDFs, reference data).
   * Does NOT touch the offline queue — pending operations are preserved.
   * Called by CacheWarmupService on the 2-day staleness interval.
   */
  async clearAllCache(): Promise<void> {
    await Promise.all([
      this.idbClear(STORE.ORDERS),
      this.idbClear(STORE.ORDER_DETAILS),
      this.idbClear(STORE.PDFS),
      this.idbClear(STORE.REFERENCE_DATA),
    ]);
  }

  /**
   * Returns the union of tkt keys stored in order-details and pdfs caches.
   * Used by CacheWarmupService to determine which tkts to delete when no longer relevant.
   */
  async getCachedTkts(): Promise<Set<string>> {
    const [detailKeys, pdfKeys] = await Promise.all([
      this.idbGetAllKeys(STORE.ORDER_DETAILS),
      this.idbGetAllKeys(STORE.PDFS),
    ]);
    return new Set([...detailKeys, ...pdfKeys]);
  }

  /** Returns only the tkt keys that have a cached PDF. */
  async getCachedPdfTkts(): Promise<Set<string>> {
    const keys = await this.idbGetAllKeys(STORE.PDFS);
    return new Set(keys);
  }
}
